import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Phone, MapPin, Home, Users, Bed } from 'lucide-react';

const STATUS_LABELS = {
  new: 'Nouveau',
  contacted: 'Contact\u00e9',
  won: 'Gagn\u00e9',
  lost: 'Perdu'
};

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-gray-100 text-gray-800'
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-CH', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

export default function LeadCard({ lead, onStatusChange, updating }) {
  return (
    <Card className="overflow-hidden" data-testid={`lead-card-${lead.id}`}>
      <CardHeader className="bg-white border-b border-[#E5E2DB]">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-xl font-semibold text-[#2B2B2B] mb-2">
              {lead.name}
            </CardTitle>
            <div className="flex flex-wrap gap-4 text-sm text-[#4A4A4A]">
              <div className="flex items-center gap-1">
                <Mail className="h-4 w-4" />
                <a href={`mailto:${lead.email}`} className="hover:text-[#8A9474]">
                  {lead.email}
                </a>
              </div>
              <div className="flex items-center gap-1">
                <Phone className="h-4 w-4" />
                <a href={`tel:${lead.phone}`} className="hover:text-[#8A9474]">
                  {lead.phone}
                </a>
              </div>
            </div>
          </div>
          <div className="ml-4">
            <Badge className={STATUS_COLORS[lead.status]}>
              {STATUS_LABELS[lead.status]}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Property Info */}
          <div>
            <h4 className="font-semibold text-[#2B2B2B] mb-3">Informations du bien</h4>
            <div className="space-y-2 text-sm text-[#4A4A4A]">
              <div className="flex items-center gap-2">
                <Home className="h-4 w-4 text-[#8A9474]" />
                <span className="capitalize">{lead.property_type}</span>
              </div>
              <div className="flex items-center gap-2">
                <Bed className="h-4 w-4 text-[#8A9474]" />
                <span>{lead.bedrooms} chambre{lead.bedrooms > 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-[#8A9474]" />
                <span>{lead.capacity} voyageur{lead.capacity > 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>

          {/* Location */}
          <div>
            <h4 className="font-semibold text-[#2B2B2B] mb-3">Localisation</h4>
            <div className="flex items-start gap-2 text-sm text-[#4A4A4A]">
              <MapPin className="h-4 w-4 text-[#8A9474] mt-0.5" />
              <div>
                <div>{lead.address}</div>
                <div>{lead.postal_code} {lead.city_full}</div>
                <div className="font-semibold text-[#8A9474] mt-1">{lead.city}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-[#E5E2DB]">
          <div className="text-xs text-[#8B8B8B]">
            Re\u00e7u le {formatDate(lead.created_at)}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#4A4A4A]">Statut:</span>
            <Select
              value={lead.status}
              onValueChange={(value) => onStatusChange(lead.id, value)}
              disabled={updating === lead.id}
            >
              <SelectTrigger className="w-40" data-testid={`lead-status-select-${lead.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Nouveau</SelectItem>
                <SelectItem value="contacted">Contact\u00e9</SelectItem>
                <SelectItem value="won">Gagn\u00e9</SelectItem>
                <SelectItem value="lost">Perdu</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
